// Argus — DownloadAllPanel: floating progress UI pentru bulk download.
// Bloc file-explorer-multiselect-download 2026-05-06.
//
// Listă scrollable cu per-file status (queued/in_progress/done/failed/skipped/cancelled),
// progress bar global, Cancel + Close.

import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress,
  List, ListItem, ListItemIcon, ListItemText, Typography, IconButton, Tooltip,
} from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import DownloadingIcon from '@mui/icons-material/Downloading';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import BlockIcon from '@mui/icons-material/Block';
import CloseIcon from '@mui/icons-material/Close';
import { formatBytes } from '../../lib/dirListing';
import type { BulkDownloadProgress, FileTaskState } from '../../lib/bulkDownload';

export interface DownloadAllPanelProps {
  open: boolean;
  progress: BulkDownloadProgress | null;
  onCancel: () => void;
  onClose: () => void;
}

export function DownloadAllPanel({ open, progress, onCancel, onClose }: DownloadAllPanelProps): JSX.Element {
  const status = progress?.status ?? 'enumerating';
  const isActive = status === 'enumerating' || status === 'downloading';
  const isDone = status === 'done';
  const isCancelled = status === 'cancelled';

  const headerText =
    isDone ? 'Download complete' :
    isCancelled ? 'Download cancelled' :
    status === 'enumerating' ? 'Enumerating…' :
    'Download in progress';

  const totalFiles = progress?.totalFiles ?? 0;
  const doneFiles = progress?.doneFiles ?? 0;
  const failedFiles = progress?.failedFiles ?? 0;
  const downloadedBytes = progress?.downloadedBytes ?? 0;
  const totalBytes = progress?.totalBytes ?? 0;
  const percent = totalBytes > 0 ? Math.min(100, (downloadedBytes / totalBytes) * 100) : 0;

  const tasks: FileTaskState[] = progress
    ? Array.from(progress.fileTasks.values())
    : [];

  return (
    <Dialog
      open={open}
      onClose={isActive ? undefined : onClose}
      maxWidth="md"
      fullWidth
      hideBackdrop
      disableEnforceFocus
      disableScrollLock
      slotProps={{
        paper: {
          sx: {
            position: 'fixed',
            bottom: 64,
            right: 24,
            margin: 0,
            maxHeight: '60vh',
            width: 520,
          },
          elevation: 8,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {headerText}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {doneFiles}/{totalFiles} done · {failedFiles} skipped/failed · {formatBytes(downloadedBytes)} / {formatBytes(totalBytes)}
          </Typography>
        </Box>
        {!isActive ? (
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        ) : null}
      </DialogTitle>
      <Box sx={{ px: 3, pb: 1 }}>
        <LinearProgress
          variant={status === 'enumerating' && totalBytes === 0 ? 'indeterminate' : 'determinate'}
          value={percent}
          color={isCancelled ? 'warning' : isDone ? 'success' : 'primary'}
        />
      </Box>
      <DialogContent dividers sx={{ p: 0 }}>
        <List dense disablePadding>
          {tasks.length === 0 ? (
            <ListItem>
              <ListItemText
                primary={<Typography variant="body2" color="text.secondary">No items queued.</Typography>}
              />
            </ListItem>
          ) : (
            tasks.map((task) => (
              <ListItem key={task.fullPath} sx={{ py: 0.25 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <StatusIcon status={task.status} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Tooltip title={task.fullPath} placement="top-start">
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {task.relativePath}
                      </Typography>
                    </Tooltip>
                  }
                  secondary={
                    <Typography variant="caption" color={task.status === 'failed' || task.status === 'skipped' ? 'warning.main' : 'text.secondary'}>
                      {formatBytes(task.size)}
                      {task.reason ? ` — ${task.reason}` : ''}
                    </Typography>
                  }
                />
              </ListItem>
            ))
          )}
        </List>
      </DialogContent>
      <DialogActions>
        {isActive ? (
          <Button onClick={onCancel} color="warning">Cancel all</Button>
        ) : (
          <Button onClick={onClose} variant="contained">Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function StatusIcon({ status }: { status: FileTaskState['status'] }): JSX.Element {
  switch (status) {
    case 'queued':
      return <HourglassEmptyIcon fontSize="small" sx={{ color: 'text.disabled' }} />;
    case 'in_progress':
      return <DownloadingIcon fontSize="small" sx={{ color: 'primary.main' }} />;
    case 'done':
      return <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />;
    case 'failed':
      return <ErrorIcon fontSize="small" sx={{ color: 'error.main' }} />;
    case 'skipped':
      return <BlockIcon fontSize="small" sx={{ color: 'warning.main' }} />;
    case 'cancelled':
      return <BlockIcon fontSize="small" sx={{ color: 'text.disabled' }} />;
    default:
      return <HourglassEmptyIcon fontSize="small" />;
  }
}
